// Serbian is digraphic: the same language is written in either the
// Cyrillic or the Latin alphabet, with a 1:1 official mapping between
// them (Gaj's Latin ↔ Vuk's Cyrillic). The frontend hosts both scripts
// in src/locales (sr-RS = Cyrillic, sr-Latn-RS = Latin), but the classic
// DCAMS screens render backend-provided `settings.text`, and that set
// only exists in Cyrillic. When a session asks for Latin-script Serbian
// we transliterate the backend text tree instead of showing Cyrillic.

// Digraph letters need case-aware output: "Љубав" → "Ljubav" but
// "ПОЉЕ" (all caps) → "POLJE".
const UPPER_DIGRAPHS = { "Љ": "Lj", "Њ": "Nj", "Џ": "Dž" };
const LOWER_DIGRAPHS = { "љ": "lj", "њ": "nj", "џ": "dž" };

const CYRILLIC_TO_LATIN = {
  А: "A", Б: "B", В: "V", Г: "G", Д: "D", Ђ: "Đ", Е: "E", Ж: "Ž",
  З: "Z", И: "I", Ј: "J", К: "K", Л: "L", М: "M", Н: "N", О: "O",
  П: "P", Р: "R", С: "S", Т: "T", Ћ: "Ć", У: "U", Ф: "F", Х: "H",
  Ц: "C", Ч: "Č", Ш: "Š",
  а: "a", б: "b", в: "v", г: "g", д: "d", ђ: "đ", е: "e", ж: "ž",
  з: "z", и: "i", ј: "j", к: "k", л: "l", м: "m", н: "n", о: "o",
  п: "p", р: "r", с: "s", т: "t", ћ: "ć", у: "u", ф: "f", х: "h",
  ц: "c", ч: "č", ш: "š",
};

const isUpperCyrillic = (ch) => ch >= "А" && ch <= "Я" || ch === "Ђ" || ch === "Ћ" || ch === "Џ" || ch === "Љ" || ch === "Њ" || ch === "Ј";

export const cyrillicToLatin = (str) => {
  if (typeof str !== "string") return str;
  let out = "";
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (UPPER_DIGRAPHS[ch]) {
      // Match the surrounding case: inside an all-caps word emit "LJ",
      // otherwise title-case "Lj". Look at the next letter first, then
      // the previous one for word-final positions like "ПАЖЊА".
      const next = str[i + 1];
      const prev = str[i - 1];
      const inAllCaps =
        (next && isUpperCyrillic(next)) ||
        (!(next && CYRILLIC_TO_LATIN[next]) && prev && isUpperCyrillic(prev));
      out += inAllCaps ? UPPER_DIGRAPHS[ch].toUpperCase() : UPPER_DIGRAPHS[ch];
    } else if (LOWER_DIGRAPHS[ch]) {
      out += LOWER_DIGRAPHS[ch];
    } else {
      out += CYRILLIC_TO_LATIN[ch] ?? ch;
    }
  }
  return out;
};

// Latin-script Serbian tags: sr-Latn, sr-Latn-RS, sr_latn… Plain `sr`
// also counts — useLocale's LANGUAGE_ALIASES routes bare `sr` to
// sr-Latn-RS, so the backend text must follow the same convention.
// Cyrillic stays for sr-RS / sr-Cyrl*.
export const isSerbianLatinTag = (tag) => {
  if (!tag || typeof tag !== "string") return false;
  const normalized = tag.toLowerCase().replace(/_/g, "-");
  if (normalized === "sr") return true;
  return normalized === "sr-latn" || normalized.startsWith("sr-latn-");
};

/**
 * Deep-copy a backend text tree with every string transliterated from
 * Serbian Cyrillic to Latin. Non-string leaves pass through untouched;
 * strings with no Cyrillic come back unchanged. Returns a new tree —
 * the original payload (which may be POSTed back to the backend) is
 * never mutated.
 */
export const serbianLatinizeTextTree = (value) => {
  if (typeof value === "string") return cyrillicToLatin(value);
  if (Array.isArray(value)) return value.map(serbianLatinizeTextTree);
  if (value && typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value)) {
      out[key] = serbianLatinizeTextTree(value[key]);
    }
    return out;
  }
  return value;
};
